<template>
  <div class="p-page-create-edit" v-if="!loading">
    <page-title
      marginBottom="0"
      subTitle="Drag and drop elements into the main page to create your bespoke survey"
    >
      <span v-if="editMode">Edit Survey</span>
      <span v-else>Create Survey</span>
    </page-title>

    <message v-if="duplicatePage" type="error">
      <h3>There's a problem</h3>
      <p>
        A survey with the slug
        <strong>{{ duplicatePage.slug }}</strong> already exists.
      </p>
    </message>

    <message
      v-if="isDraft && currentSurvey.status !== 'pending'"
      type="warning"
    >
      <h3>You are editing a draft</h3>
      <p>
        The changes below are currently saved as a draft copy, and will not
        appear on the intranet until the page has been submitted.
      </p>
    </message>
    <section-block :headerOverflowHidden="true">
      <template slot="header">
        <form-field
          dynamic-width
          ghost
          large
          min-width="312px"
          v-model="currentSurvey.current_version.title"
          v-validate="{ required: true }"
          name="title"
          placeholder="Name your survey here..."
          :error="errors.first('title')"
          is-inline
        ></form-field>
      </template>

      <template slot="actions">
        <base-button
          v-if="editMode && currentVersion.status !== 'draft'"
          @click="onClickViewSurvey"
          icon-before="search"
          >View Survey</base-button
        >
        <base-button
          v-if="
            editMode &&
              currentVersion.version ===
                currentSurvey.current_version.version &&
              currentVersion.status !== 'draft'
          "
          primary="true"
          @click="onClickUpdate"
          icon-before="edit"
          >Update</base-button
        >
        <base-button
          v-if="
            !currentSurvey.current_version ||
              !editMode ||
              currentVersion.status == 'draft'
          "
          @click="onClickDraft"
          >Save Draft</base-button
        >
        <base-button
          v-if="
            !editMode || (currentVersion.status == 'draft' && userCanApprove)
          "
          primary="true"
          @click="onClickPublish"
          >Publish</base-button
        >
        <base-button
          icon-before="back"
          v-if="
            editMode &&
              currentVersion.version !==
                currentSurvey.current_version.version &&
              userCanApprove
          "
          primary="true"
          @click="onClickReinstate"
          >Re-instate</base-button
        >
      </template>

      <page-builder
        :pageId="articleid"
        @reviewDateUpdated="(newDate) => (articlereview_date = newDate)"
        :activeSbPanel="activeSbPanel"
        type="survey"
        :load-pages="false"
        :files-enabled="filesEnabled"
      >
        <template slot="intro">
          <div class="c-pb__no-elements--landscape">
            <div class="c-pb__no-elements-text">
              <p>Ready to add fields to your survey?</p>
              <p>
                Select
                <strong>&lsquo;Customise Survey&rsquo;</strong> tab in the right
                hand menu
              </p>
            </div>
            <inline-svg name="page-builder-landscape"></inline-svg>
          </div>
        </template>
        <tabs slot="tabs">
          <tabs-item
            :active="activeSbPanel === 'settings'"
            @click="activeSbPanel = 'settings'"
            >Survey Settings</tabs-item
          >

          <tabs-item
            :active="activeSbPanel === 'elements'"
            @click="activeSbPanel = 'elements'"
            >Customise Survey</tabs-item
          >
        </tabs>

        <div slot="sidebars">
          <div class="c-pb__sb-settings" v-show="activeSbPanel === 'settings'">
            <form-field
              full-width
              label="Survey Slug"
              name="survey_slug"
              type="text"
              v-model="currentSurvey.survey_url"
            ></form-field>

            <form-field
              label="Category"
              name="category"
              helper-text="Select the category you want the survey to appear in"
              :required="true"
            >
              <form-type-ahead-select
                v-model="currentCategory"
                id="category_id"
                name="category_id"
                :options="orderedCategories"
                async-action="SurveyCategories/quickList"
                display-field="name"
                @input="onCategoryInput"
                v-if="!loading"
                :disabled="categoryDisabled"
              />
              <p v-else>Loading categories</p>
              <p v-if="!!editMode && !!version">
                <small
                  >Once a survey is published, you can not change its
                  category.</small
                >
              </p>
            </form-field>

            <form-field
              label="Responsible Users"
              name="responsible_users"
              required
              helper-text="Give people responsibility of this form"
            >
              <div class="row">
                <div class="col-xs-12">
                  <form-label small required>Primary</form-label>
                  <user-select
                    v-model="currentSurvey.primary_user"
                    id="survey_primary_user"
                    name="survey_primary_user"
                    v-validate="{ required: true }"
                    data-vv-as="primary user"
                    :error="errors.first('survey_primary_user')"
                    @input="markUserChange"
                  ></user-select>
                </div>
                <div class="col-xs-12">
                  <form-label small required>Secondary</form-label>
                  <user-select
                    v-model="currentSurvey.secondary_user"
                    id="survey_secondary_user"
                    name="survey_secondary_user"
                    v-validate="{
                      required: true,
                      is_not: currentSurvey.primary_user_id,
                    }"
                    data-vv-as="secondary user"
                    :error="errors.first('survey_secondary_user')"
                    @input="markUserChange"
                  ></user-select>
                </div>
              </div>
            </form-field>

            <!-- Add Tags -->
            <form-field
              label="Add Tags"
              name="document_tags"
              :helper-text="
                'Help users find your survey by tagging relevant keywords'
              "
              v-if="!true"
            >
              <form-multiselect
                :show-valid-tick="true"
                v-model="currentSurvey.tags"
                :options="tags"
                :multiple="true"
                label="name"
                track-by="name"
                :taggable="true"
                @tag="addTag"
              ></form-multiselect>
            </form-field>

            <form-field
              :label="versionLabels.label"
              :name="versionLabels.document_version_number"
              :helper-text="versionLabels.helper"
              v-model="version"
              v-validate="{ required: true, min_value: 1, decimal: 1 }"
              :data-vv-as="versionLabels.vvAs"
              :error="errors.first(versionLabels.name)"
              required
            ></form-field>

            <form-field
              label="Anonymise responses"
              name="anonymous"
              helper-text="Selecting this will prevent users from seeing specific user information in the results"
            >
              <form-toggle v-model="currentSurvey.anonymous" />
            </form-field>

            <form-field
              label="Allow multiple responses?"
              name="multiple"
              helper-text="Determine if a user can submit multiple entries, or only submit once"
            >
              <form-toggle v-model="currentSurvey.multiple" />
            </form-field>

            <form-field
              label="Private"
              name="private"
              helper-text="Do you want the survey results to be private?"
            >
              <form-toggle v-model="currentSurvey.private" />
            </form-field>

            <user-group-select
              @onChange="onUserAdd"
              v-if="currentSurvey.private"
              :value="selectedUsers"
            />
          </div>
        </div>
      </page-builder>
    </section-block>

    <modal
      v-if="downloadWarning"
      show-close-button
      @close="downloadWarning = false"
    >
      <h2 slot="header">Sorry</h2>

      <p>
        Currently, you can only add one file upload field to a survey.
      </p>
      <p>
        Please update your survey, making sure you only have one file upload
        field, and try to save again.
      </p>
    </modal>
  </div>

  <loading v-else></loading>
</template>

<script>
/**
 * TODO: This is based on the page builder for content, but needs altering to
 * fit the needs of the news section.  Ideally the same 'could' be used for events
 */
import PageBuilder from "@/components/PageBuilder";
import { FormToggle, UserGroupSelect } from "@/components/forms";
import { mapState, mapGetters } from "vuex";
import FormTypeAheadSelect from "@/components/FormTypeAheadSelect";
import UserSelect from "@/components/UserSelect";
import UserHasPermission from "@/mixins/UserHasPermission";

export default {
  name: "admin-page-edit-create",

  components: {
    PageBuilder,
    FormTypeAheadSelect,
    UserSelect,
    UserGroupSelect,
    FormToggle,
  },

  mixins: [UserHasPermission],

  data() {
    return {
      editMode: false,
      loading: false,
      duplicatePage: null,
      activeSbPanel: "settings",
      articlestatus: "",
      articleid: 0,
      survey: {},
      image_uploaded: false,
      docType: {
        plural: "surveys",
        single: "survey",
      },
      show_date: false,
      originalPageElements: {},
      version: "1.0",
      originalLink: "",
      selected_users: [],
      filesEnabled: true,
      downloadWarning: false,
    };
  },

  beforeRouteLeave(to, from, next) {
    if (this.elementsHaveChanged && this.editMode) {
      this.$store.dispatch("createDialog", {
        messageText:
          "Any changes you made will be lost if you navigate away from this page, are you sure?",
        confirm: () => {
          this.$store.commit("pageElements/setType", "content");
          next();
        },
      });
    } else {
      this.$store.commit("pageElements/setType", "content");
      next();
    }
  },

  async created() {
    this.loading = true;
    // reset
    this.$store.commit("pageElements/setPageElements", []);
    this.$store.commit("pageElements/setType", "form");
    await this.$store.dispatch("SurveyCategories/list");
    await this.$store.dispatch("tags/list");

    const { id, versionId } = this.$route.params || null;

    if (id) {
      await this.loadSurvey(id, versionId);
    } else {
      this.$store.dispatch("Surveys/reset");
      this.loading = false;
    }
  },

  methods: {
    onUserAdd(value) {
      this.selected_users = [];
      this.currentSurvey.users = [];
      value.forEach((val) => {
        //if we are a group, we need to extract the individual user ids
        if (val.id.toString().includes("grp")) {
          val = val.meta.data;
          this.currentSurvey.users = [...this.currentSurvey.users, ...val];
        } else {
          this.currentSurvey.users.push(val);
        }
        this.selected_users.push(val);
      });
    },
    async loadSurvey(surveyId, versionId) {
      await this.$store.dispatch("Surveys/read", { surveyId, versionId });
      // if there is no version, look to see if there is a draft
      if (!this.currentSurvey.current_version) {
        try {
          const version = await this.getLatestDraft();
          this.currentSurvey.current_version = version;
          console.log(
            "TCL:  ~ file: AddEdit.vue ~ line 316 ~ loadSurvey ~ version",
            version
          );
          await this.$store.dispatch("Surveys/getVersion", {
            surveyId,
            versionId: version.id,
          });
        } catch (err) {}
      }
      this.retrieveSurveyElements(
        surveyId,
        versionId ? versionId : this.currentSurvey.current_version.id
      );
      if (this.currentSurvey.category_id) {
        this.currentSurvey.category_id = this.currentSurvey.category.id;
      }
      this.loading = false;
      this.editMode = true;
      this.originalLink = this.currentSurvey.survey_url;
      this.currentSurvey.survey_url = this.currentSurvey.survey_url
        .split("/")
        .slice(-1);
      this.version = this.currentVersion.version;
    },
    async getLatestDraft() {
      const versions = await this.$store.dispatch(
        "Surveys/listVersions",
        this.currentSurvey.id
      );
      const { version } = versions.data.data;
      const latestDraft = version
        .filter((vers) => vers.status == "draft")
        .sort((a, b) => a.id - b.id)[0];
      return latestDraft;
    },
    markUserChange(val) {
      // this.currentSurvey.primary_user_id = val.id
    },
    onCategoryInput(val) {
      this.currentSurvey.category_id = val.id;
    },
    updateSlug(val) {
      this.currentSurvey.slug = val;
    },

    onClickReinstate() {
      this.$store.dispatch("createDialog", {
        // TODO: This does the right thing, but do we need to create its own
        // method in the store so we can alter the confirmation etc.?
        confirm: () => {
          this.$store
            .dispatch("Surveys/approveSurvey", {
              surveyId: this.$route.params.id,
              versionId: this.$route.params.versionId,
            })
            .then((resp) => {
              this.loadSurvey(
                this.$route.params.id,
                this.$route.params.versionId
              );
            });
        },
      });
    },

    retrieveSurveyElements(page, version = null) {
      let { elements } = this.currentVersion;
      console.log(
        "TCL:  ~ file: AddEdit.vue ~ line 371 ~ retrieveSurveyElements ~ this.currentVersion",
        this.currentVersion
      );
      console.log(
        "TCL:  ~ file: AddEdit.vue ~ line 368 ~ retrieveSurveyElements ~ elements",
        elements
      );

      if (version) {
        // elements = this.$store.state.Forms.versionElements
      }

      if (!elements) {
        elements = [];
        console.log(
          "TCL:  ~ file: AddEdit.vue ~ line 380 ~ retrieveSurveyElements ~ elements",
          elements
        );
      }

      this.$store.commit("pageElements/setPageElements", elements);

      this.originalPageElements = elements ? elements.slice() : [];
    },

    onClickViewSurvey() {
      window.open(`/survey${this.originalLink}`);
    },

    onClickDraft() {
      this.onClickPublish(true);
    },

    onClickPublish(saveAsDraft = false) {
      // For testing, we just need to post the JSON somewhere
      const vm = this;

      // if we have more then 1 file upload we need to stop
      if (this.fileUploadCount > 1) {
        this.downloadWarning = true;
        return;
      }

      this.$validator.validate().then((result) => {
        if (!result) {
          vm.showValidationMessage = true;
        } else {
          vm.showValidationMessage = false;
          vm.currentSurvey.current_version.elements = [
            ...vm.$store.state.pageElements.pageElements,
          ];

          //set ids for primary and secondary users
          vm.currentSurvey.primary_user_id = vm.currentSurvey.primary_user.id;
          vm.currentSurvey.secondary_user_id =
            vm.currentSurvey.secondary_user.id;
          vm.currentSurvey.current_version.version = vm.version;
          vm.currentSurvey.category = vm.currentSurvey.category_id;

          // users
          let userIds = [];
          if (vm.currentSurvey.users) {
            userIds = vm.currentSurvey.users.map((usr) => {
              if (typeof usr === "number") {
                return usr;
              }
              return usr.id;
            });
          }

          vm.currentSurvey.user_ids = userIds;

          //only need certain fields
          const {
            id,
            status,
            primary_user,
            secondary_user,
            self,
            user_favourite,
            favourite_count,
            created_user,
            created_time,
            modified_user,
            modified_time,
            users,
            ...payload
          } = vm.currentSurvey;

          payload.description = "";

          const action = this.editMode
            ? "Surveys/update"
            : "Surveys/createAndPublish";
          const dataToSend = this.editMode
            ? {
                pageData: payload,
                surveyId: this.currentSurvey.id,
                saveAsDraft,
              }
            : { saveAsDraft, payload };

          this.$store
            .dispatch(action, dataToSend)
            .then((page) => {
              vm.loading = false;
              this.$store.commit("pageElements/setPageElements", []);
              // If we are a super admin, publish the page
              // if (this.userCanApprove) {
              //   this.$store.dispatch("pages/approve", page.id).then(resp => {
              //     this.$router.push({
              //       name: "AdminContentPageEdit",
              //       params: { pageId: page.id }
              //     });
              //   });
              // } else {
              this.$router.push({
                name: "AdminSurveyEditPage",
                params: { id: page.document.data.data.survey[0].id },
              });
              // }
            })
            .catch((error) => {
              console.log(
                "TCL:  ~ file: AddEdit.vue ~ line 428 ~ this.$validator.validate ~ error",
                error
              );
              vm.loading = false;
              if (error.meta && error.meta.duplicate_page) {
                this.duplicatePage = error.meta.duplicate_page;
              }
            });
        }
      });
    },

    onClickUpdate(saveAsDraft = false) {
      const { currentSurvey } = this;

      if (this.fileUploadCount > 1) {
        this.downloadWarning = true;
        return;
      }
      this.loading = true;

      const vm = this;
      this.$validator.validate().then((result) => {
        if (!result) {
          vm.showValidationMessage = true;
          this.loading = false;
        } else {
          if (
            this.elementsHaveChanged &&
            this.version == currentSurvey.current_version.version
          ) {
            this.loading = false;
            this.$store.dispatch(
              "displayApiError",
              "You need to change the version number to proceed"
            );
            return;
          } else {
            const pageElments = vm.$store.getters["pageElements/forServer"];
            const versionId = currentSurvey.current_version.version;
            const editedForm = JSON.parse(JSON.stringify(currentSurvey));

            // Having issues with the original version id carrying over so..f
            editedForm.current_version.orig_version = versionId;

            // add elements to the current page.
            editedForm.current_version.elements = pageElments;

            // interest groups need to be just ids
            if (!editedForm.interestgroup_ids && editedForm.interestgroups) {
              editedForm.interestgroup_ids = editedForm.interestgroups.map(
                (group) => group.id
              );
            }
            // then remove the interest group bit
            delete editedForm.interestgroups;

            if (!editedForm.current_version.title) {
              editedForm.title = "Untitled Survey";
            }

            let action = "Surveys/resubmit";

            //set ids for primary and secondary users
            editedForm.primary_user_id = editedForm.primary_user.id;
            editedForm.secondary_user_id = editedForm.secondary_user.id;
            editedForm.current_version.version = editedForm.version;
            editedForm.category = editedForm.category.id;

            // users
            let userIds = [];
            if (editedForm.users) {
              userIds = editedForm.users.map((usr) => {
                if (typeof usr === "number") {
                  return usr;
                }
                return usr.id;
              });
            }

            editedForm.user_ids = userIds;

            //only need certain fields
            const {
              id,
              status,
              primary_user,
              secondary_user,
              self,
              user_favourite,
              favourite_count,
              created_user,
              created_time,
              modified_user,
              modified_time,
              users,
              ...payload
            } = editedForm;

            this.$store
              .dispatch(action, {
                surveyId: vm.currentSurvey.id,
                pageData: payload,
                version: vm.version,
                saveAsDraft,
              })
              .then((response) => {
                vm.page = response;
                vm.loading = false;
              })
              .catch((error) => {
                vm.loading = false;
                if (error.meta.duplicate_page) {
                  vm.duplicatePage = error.meta.duplicate_page;
                }
              });
          }
        }
      });
    },
    addTag() {},
  },

  computed: {
    ...mapState("Surveys", ["currentSurvey"]),
    ...mapGetters("SurveyCategories", ["orderedCategories"]),
    ...mapState({
      tags: (state) => state.tags.collection,
    }),
    userCanApprove() {
      return this.userHasPermission("approve_surveys");
    },
    fileUploadCount() {
      const elems = this.$store.getters["pageElements/forServer"];
      const fieldsTotal = elems.filter((elem) =>
        ["media-file-upload", "media-file"].includes(elem.type)
      ).length;

      const rows = elems.filter((elem) => elem.type == "base-row");
      let rowsTotal = 0;

      if (rows.length > 0) {
        // go through each row to see if it has a file upload field
        rows.forEach((row) => {
          const { leftColumn, rightColumn } = row.properties;
          if (
            ["media-file", "media-file-upload"].includes(
              JSON.parse(leftColumn).type
            )
          ) {
            rowsTotal += 1;
          }
          if (
            ["media-file", "media-file-upload"].includes(
              JSON.parse(rightColumn).type
            )
          ) {
            rowsTotal += 1;
          }
        });
      }

      return fieldsTotal + rowsTotal;
    },
    selectedUsers: {
      get() {
        return this.currentSurvey.users;
      },
      set(val) {
        return val;
      },
    },
    isDraft() {
      return this.currentSurvey.current_version.status == "draft";
    },
    currentVersion() {
      return this.currentSurvey.current_version;
    },
    categoryDisabled() {
      // !!editMode && !!version
      return this.editMode;
    },
    versionLabels() {
      if (this.editMode) {
        return {
          label: "Version Number",
          name: "document_version_number",
          helper: "Set the version number for this survey",
          vvAs: "version number",
        };
      }

      return {
        label: "Initial Version Number",
        name: "document_starting_version",
        helper: "Set the starting version number for the survey",
        model: this.currentSurvey.current_version.version,
        vvAs: "initial version number",
      };
    },
    newVersion: {
      get() {
        return this.currentSurvey.current_version.version || "1.0";
      },
      set(newVal, oldVal) {
        this.currentSurvey.new_version = newVal;
      },
    },
    // TODO: This has been copied from the pageElements
    elementsHaveChanged() {
      const serverEls = this.$store.getters["pageElements/forServer"];
      try {
        if (this.originalPageElements.length !== serverEls.length) {
          return true;
        }

        for (let i = this.originalPageElements.length; i--; ) {
          if (this.originalPageElements[i] !== serverEls[i]) {
            return true;
          }
        }
      } catch (err) {
        // console.log(err.message);
      }

      return false;
    },
    elementList() {
      return this.currentSurvey.elements;
    },
    currentCategory: {
      get() {
        if (!this.currentSurvey.category && !this.currentSurvey.category_id) {
          return null;
        }
        const value = this.currentSurvey.category_id
          ? this.currentSurvey.category_id
          : this.currentSurvey.category.id;
        const current = this.orderedCategories.filter(
          (item) => item.id === value
        );

        return current.length > 0 ? current[0] : null;
      },
      set(newVal, oldVal) {
        this.currentSurvey.category_id = newVal.id;
        return newVal;
      },
    },
  },
  watch: {
    fileUploadCount: {
      immediate: true,
      deep: true,
      handler(newValue) {
        this.filesEnabled = newValue < 1;
      },
    },
  },
};
</script>

<style lang="scss">
.p-page-create-edit {
  .c-section-block__header-main .c-form__group {
    margin: 0;
  }
}

.default-image-holder {
  min-height: 300px;
  margin-top: 20px;
  background: $colour-nhs-pale-grey;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  width: 100%;

  .c-media-image {
    background: transparent;
  }

  .default_image__preview {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: 0 0;
    z-index: 0;
    &:after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      content: "";
      background: rgba(0, 0, 0, 0.3);
    }
  }

  .default_image__button {
    position: relative;
    z-index: 1;
  }
}

.c-pb__no-elements--landscape {
  position: relative;

  .c-inline-svg,
  .c-pb__no-elements-text {
    position: absolute;
  }

  .c-pb__no-elements-text {
    text-align: left;
    left: 33%;
    z-index: 5;
  }
}
</style>
